/**
 * @format
 */
import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { Observable } from "rxjs/index";
import {
   PreviewTemplateState,
   ReloadPreviewTemplate,
   STORE_FEATURE_KEY_PREVIEW_TEMPLATE,
   TemplateUpdateModel,
} from "./preview-edit-template.reducer";
import { map } from "rxjs/operators";
import { TemplateParametersService } from "../../template-parameters/template-parameters.service";
import { EditorService } from "../../ng-editor/services/editor.service";

const previewTemplateState = (appState: any): PreviewTemplateState => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE];
const loadingSelector = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].loading;
const getModel = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].previewEditTemplate;
const getContextVariables = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].contextVariables;
const getContentResponse = (previewTemplateState: PreviewTemplateState) => previewTemplateState.contentResponse;
const getSourceLoading = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].sourceLoading;
const getValidatedSource = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].validatedSource;
const getSavedCustomTemplate = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].savedCustomTemplate;
const getTemplateLastModified = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].templateLastModified;
const getLoadPreviewContent = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].loadPreviewContent;
const getLoadingMessageContentByTemplateId = (appState: any) =>
   appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].loadingMessageContentByTemplateId;
const getTemplateParameters = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].templateParameters;
const getLocations = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].locations;
const getLocationEntities = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].locationEntities;
const getProviders = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].providers;
const isModelProcessed = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].isModelProcessed;

const getTemplateUpdateModel = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].templateUpdateModel;
const getMediaDevices = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].mediaDevices;
const getErrorMessage = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].errorMessage;
const getTotalStrategiesMapped = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].totalStrategies;
const isMediaProcessed = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].isMediaProcessed;

const getReloadPreviewTemplate = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].reloadPreviewTemplate;

const getTemplateState = (appState: any) => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].templateState;

const isSavingSelector = (appState: any): boolean => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].isSaving;
const getSmsPart = (appState: any): number => appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].smsParts;
const getSmsParsedTextLength = (appState: any): number =>
   appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].smsParsedTextLength;
const getEmailPreventChanges = (appState: any): boolean =>
   appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].emailPreventChanges;
const getMessagePreventChanges = (appState: any): boolean =>
   appState[STORE_FEATURE_KEY_PREVIEW_TEMPLATE].messagePreventChanges;

@Injectable()
export class PreviewEditTemplateSelectors {
   constructor(
      private store: Store<any>,
      private templateParametersService: TemplateParametersService,
      private editorService: EditorService
   ) {}

   selectLoading(): Observable<boolean> {
      return this.store.pipe(select(loadingSelector));
   }

   selectGetModel(): Observable<any> {
      return this.store.pipe(select(getModel));
   }

   selectGetContextVariable(): Observable<any> {
      return this.store.pipe(select(getContextVariables));
   }

   selectGetContent() {
      return this.store.pipe(select(previewTemplateState), select(getContentResponse)).pipe(
         map((value) => {
            if (Boolean(value) && Boolean(value.mailContent)) {
               let mailContentRectified = this.editorService.getInnerHtmlContentRectified(value.mailContent);
               mailContentRectified = this.templateParametersService.wrapParametersWithSpan(mailContentRectified);
               return { ...value, mailContent: mailContentRectified };
            }
            return value;
         })
      );
   }

   selectGetSourceLoading(): Observable<any> {
      return this.store.pipe(select(getSourceLoading));
   }

   selectGetValidatedSource(): Observable<any> {
      return this.store.pipe(select(getValidatedSource));
   }

   selectGetSavedCustomTemplate(): Observable<any> {
      return this.store.pipe(select(getSavedCustomTemplate));
   }

   selectGetTemplateLastModified(): Observable<any> {
      return this.store.pipe(select(getTemplateLastModified));
   }

   selectedIsMediaProcessed(): Observable<boolean> {
      return this.store.pipe(select(isMediaProcessed));
   }

   selectedLoadingMessageContentByTemplateId(): Observable<boolean> {
      return this.store.pipe(select(getLoadingMessageContentByTemplateId));
   }

   selectGetTemplateParameters(): Observable<any> {
      return this.store.pipe(select(getTemplateParameters));
   }

   selectGetLocations(): Observable<any> {
      return this.store.pipe(select(getLocations));
   }

   selectGetLocationEntities(): Observable<any> {
      return this.store.pipe(select(getLocationEntities));
   }

   selectGetProviders(): Observable<any> {
      return this.store.pipe(select(getProviders));
   }

   selectIsModelProcessed(): Observable<boolean> {
      return this.store.pipe(select(isModelProcessed));
   }

   selectTemplateUpdateModel(): Observable<TemplateUpdateModel> {
      return this.store.pipe(select(getTemplateUpdateModel));
   }

   selectGetMediaDevices(): Observable<any> {
      return this.store.pipe(select(getMediaDevices));
   }

   selectErrorMessage(): Observable<string> {
      return this.store.pipe(select(getErrorMessage));
   }

   selectTotalStrategiesMapped(): Observable<number> {
      return this.store.pipe(select(getTotalStrategiesMapped));
   }

   selectReloadPreviewTemplate(): Observable<ReloadPreviewTemplate> {
      return this.store.pipe(select(getReloadPreviewTemplate));
   }

   selectGetTemplateState(): Observable<any> {
      return this.store.pipe(select(getTemplateState));
   }

   selectIsSaving(): Observable<boolean> {
      return this.store.pipe(select(isSavingSelector));
   }

   selectGetSmsParts(): Observable<number> {
      return this.store.pipe(select(getSmsPart));
   }

   selectGetSmsParsedTextLength(): Observable<number> {
      return this.store.pipe(select(getSmsParsedTextLength));
   }

   selectGetEmailPreventChanges(): Observable<boolean> {
      return this.store.pipe(select(getEmailPreventChanges));
   }
   selectGetMessagePreventChanges(): Observable<boolean> {
      return this.store.pipe(select(getMessagePreventChanges));
   }
}
